import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { createContact, getMyContacts, uploadContactAttachment } from '@/lib/api';
import { Send, MessageSquare, X, UploadCloud } from 'lucide-react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { getImageFileFromClipboard } from '@/utils/pasteImage';
import { autoResizeTextarea } from '@/utils/autoResize';
import { getInputSurface, getPanelStyle, isDarkTheme, pickTextColor, withAlpha } from '@/lib/themeStyles';

export function ContactPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [category, setCategory] = useState('General');
    const [message, setMessage] = useState('');
    const messageRef = useRef<HTMLTextAreaElement>(null);
    const [inquiryNumber, setInquiryNumber] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [history, setHistory] = useState<any[]>([]);
    const [activeTab, setActiveTab] = useState<'write' | 'history'>('write');
    const [attachments, setAttachments] = useState<string[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [attachmentError, setAttachmentError] = useState('');
    const [highlightContactId, setHighlightContactId] = useState<number | null>(null);

    // @ts-ignore
    const { theme } = useOutletContext<{ theme: any }>();
    const darkTheme = isDarkTheme(theme);
    const panelStyle = getPanelStyle(theme, 0.96, 0.9);
    const inputSurface = getInputSurface(theme);

    useEffect(() => {
        fetchHistory();
    }, []);

    useEffect(() => {
        const rawContactId = searchParams.get('contact_id');
        if (!rawContactId) return;
        const contactId = Number(rawContactId);
        const next = new URLSearchParams(searchParams);
        next.delete('contact_id');
        setSearchParams(next, { replace: true });
        if (!Number.isFinite(contactId) || contactId <= 0) return;
        setHighlightContactId(contactId);
        setActiveTab('history');
    }, [searchParams, setSearchParams]);

    useEffect(() => {
        autoResizeTextarea(messageRef.current);
    }, [message]);

    useEffect(() => {
        if (!highlightContactId || activeTab !== 'history') return;
        const target = document.getElementById(`contact-history-${highlightContactId}`);
        if (target) {
            target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        const timer = window.setTimeout(() => setHighlightContactId(null), 3500);
        return () => window.clearTimeout(timer);
    }, [highlightContactId, activeTab, history]);

    const fetchHistory = async () => {
        try {
            const res = await getMyContacts('inquiry');
            setHistory(res.data);
        } catch (e) {
            console.error(e);
        }
    };

    const handleInquiryNumberChange = (value: string) => {
        const digitsOnly = value.replace(/[^0-9]/g, '').slice(0, 8);
        setInquiryNumber(digitsOnly);
    };

    const isVideoUrl = (url: string) => /\.(mp4|webm|mov|m4v)$/i.test(url.split('?')[0]);

    const handleAttachmentChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setIsUploading(true);
        setAttachmentError('');

        try {
            const uploaded: string[] = [];
            for (const file of files) {
                if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
                    continue;
                }
                const res = await uploadContactAttachment(file);
                if (res.data?.url) {
                    uploaded.push(res.data.url);
                }
            }
            if (uploaded.length) {
                setAttachments((prev) => [...prev, ...uploaded]);
            }
        } catch (err) {
            console.error(err);
            setAttachmentError('添付のアップロードに失敗しました。');
        } finally {
            setIsUploading(false);
            e.target.value = '';
        }
    };

    const handlePasteAttachment = async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
        const imageFile = getImageFileFromClipboard(e);
        if (!imageFile) return;
        e.preventDefault();
        if (!imageFile.type.startsWith('image/')) return;
        setIsUploading(true);
        setAttachmentError('');
        try {
            const res = await uploadContactAttachment(imageFile);
            if (res.data?.url) {
                setAttachments((prev) => [...prev, res.data.url]);
            }
        } catch (err) {
            console.error(err);
            setAttachmentError('添付のアップロードに失敗しました。');
        } finally {
            setIsUploading(false);
        }
    };

    const removeAttachment = (url: string) => {
        setAttachments((prev) => prev.filter((item) => item !== url));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!message.trim()) return;

        setIsSubmitting(true);
        setError('');

        try {
            // No recipient_id = Inquiry
            await createContact(category, message, undefined, inquiryNumber.trim() || undefined, attachments);
            setMessage('');
            setInquiryNumber('');
            setAttachments([]);
            fetchHistory();
            setActiveTab('history');
        } catch (err) {
            console.error(err);
            setError('送信に失敗しました。');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto mt-8 p-4">
            <h1 className="text-3xl font-serif font-bold mb-6 tracking-widest text-center" style={{ color: theme?.colors?.text }}>
                管理人へ連絡
            </h1>
            <p className="text-center opacity-70 mb-8 max-w-2xl mx-auto">
                お困りごとやご質問がございましたら、こちらからお問い合わせください。<br />
                管理人が順次対応いたします。
            </p>

            {/* Tabs */}
            <div className="flex justify-center gap-8 mb-8 border-b border-opacity-20" style={{ borderColor: theme?.colors?.border }}>
                <button
                    onClick={() => setActiveTab('write')}
                    className={`pb-2 px-8 font-serif font-bold transition-all duration-300 tracking-wider ${activeTab === 'write' ? 'border-b-2 opacity-100' : 'opacity-40 hover:opacity-70'}`}
                    style={{
                        borderColor: activeTab === 'write' ? theme?.colors?.primary : 'transparent',
                        color: theme?.colors?.text
                    }}
                >
                    問い合わせ
                </button>
                <button
                    onClick={() => setActiveTab('history')}
                    className={`pb-2 px-8 font-serif font-bold transition-all duration-300 tracking-wider ${activeTab === 'history' ? 'border-b-2 opacity-100' : 'opacity-40 hover:opacity-70'}`}
                    style={{
                        borderColor: activeTab === 'history' ? theme?.colors?.primary : 'transparent',
                        color: theme?.colors?.text
                    }}
                >
                    履歴
                </button>
            </div>

            {activeTab === 'write' ? (
                <form onSubmit={handleSubmit} className=" backdrop-blur-md p-8 rounded-xl shadow-lg border"
                    style={panelStyle}>
                    <div className="space-y-6">
                        <div>
                            <label className="block text-sm font-medium mb-2 font-serif tracking-wide" style={{ color: theme?.colors?.text }}>カテゴリ</label>
                            <select
                                value={category}
                                onChange={(e) => setCategory(e.target.value)}
                                className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2"
                                style={{ borderColor: theme?.colors?.border, backgroundColor: inputSurface, color: theme?.colors?.text }}
                            >
                                <option value="General">一般的なお問い合わせ</option>
                                <option value="Login Issue">ログイン・アカウントについて</option>
                                <option value="Bug Report">不具合の報告</option>
                                <option value="Other">その他</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-2 font-serif tracking-wide" style={{ color: theme?.colors?.text }}>
                                問い合わせ番号（任意・8桁）
                            </label>
                            <input
                                type="text"
                                value={inquiryNumber}
                                onChange={(e) => handleInquiryNumberChange(e.target.value)}
                                inputMode="numeric"
                                maxLength={8}
                                pattern="[0-9]*"
                                className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2"
                                placeholder="前の質問の続きの場合は、8桁の番号を同じユーザー名・同じメールアドレスと一緒に入力してください"
                                style={{ borderColor: theme?.colors?.border, backgroundColor: inputSurface, color: theme?.colors?.text }}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-2 font-serif tracking-wide" style={{ color: theme?.colors?.text }}>内容</label>
                            <textarea
                                ref={messageRef}
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                onInput={(e) => autoResizeTextarea(e.currentTarget)}
                                onPaste={handlePasteAttachment}
                                rows={6}
                                required
                                className="w-full p-4 border rounded-lg focus:outline-none focus:ring-2 resize-none"
                                placeholder="お問い合わせ内容を入力してください..."
                                style={{ borderColor: theme?.colors?.border, backgroundColor: inputSurface, color: theme?.colors?.text }}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-2 font-serif tracking-wide" style={{ color: theme?.colors?.text }}>
                                添付（任意・画像/動画）
                            </label>
                            <div className="flex items-center gap-3">
                                <label
                                    htmlFor="contact-attachment"
                                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm cursor-pointer"
                                    style={{ borderColor: theme?.colors?.border, color: theme?.colors?.text }}
                                >
                                    <UploadCloud size={16} />
                                    追加する
                                </label>
                                <input
                                    id="contact-attachment"
                                    type="file"
                                    multiple
                                    accept="image/*,video/*"
                                    onChange={handleAttachmentChange}
                                    className="hidden"
                                />
                                {isUploading && <span className="text-xs opacity-70">アップロード中...</span>}
                            </div>
                            {attachmentError && <div className="text-red-500 text-xs mt-2">{attachmentError}</div>}
                            {attachments.length > 0 && (
                                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {attachments.map((url) => (
                                        <div key={url} className="relative border rounded-lg overflow-hidden" style={{ borderColor: theme?.colors?.border }}>
                                            {isVideoUrl(url) ? (
                                                <video src={url} controls className="w-full max-h-40 bg-black" />
                                            ) : (
                                                <img src={url} alt="Attachment" className="w-full max-h-40 object-cover" />
                                            )}
                                            <button
                                                type="button"
                                                onClick={() => removeAttachment(url)}
                                                className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1"
                                            >
                                                <X size={14} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {error && <div className="text-red-500 text-sm">{error}</div>}

                        <div className="flex justify-end">
                            <Button
                                type="submit"
                                disabled={isSubmitting || isUploading || !message.trim()}
                                className="w-full sm:w-auto gap-2 font-serif tracking-widest"
                                style={{ backgroundColor: theme?.colors?.primary, color: pickTextColor(theme?.colors?.primary || '#000000') }}
                            >
                                {isSubmitting ? '送信中...' : '送信する'}
                                <Send size={16} />
                            </Button>
                        </div>
                    </div>
                </form>
            ) : (
                <div className="space-y-4">
                    {history.length === 0 ? (
                        <p className="text-center opacity-50">お問い合わせ履歴はありません。</p>
                    ) : (
                        history.map(item => (
                            <div
                                key={item.contact_id}
                                id={`contact-history-${item.contact_id}`}
                                className={`p-4 rounded-xl border backdrop-blur-sm relative transition-all ${
                                    highlightContactId === item.contact_id ? 'ring-2' : ''
                                }`}
                                style={{
                                    backgroundColor: withAlpha(theme?.colors?.card || '#ffffff', darkTheme ? 0.84 : 0.76),
                                    borderColor: theme?.colors?.border,
                                    ...(highlightContactId === item.contact_id ? { '--tw-ring-color': theme?.colors?.primary } : {}),
                                } as any}>
                                <div className="flex justify-between items-start mb-2">
                                    <div className="font-bold flex items-center gap-2" style={{ color: theme?.colors?.primary }}>
                                        <MessageSquare size={16} />
                                        <span>To: Administrator</span>
                                        <span className="text-xs opacity-50 font-normal">
                                            {new Date(item.created_at).toLocaleString()}
                                        </span>
                                    </div>
                                    {item.is_resolved && (
                                        <span
                                            className="text-xs px-2 py-1 rounded-full border"
                                            style={{
                                                backgroundColor: withAlpha(theme?.colors?.primary || '#000000', darkTheme ? 0.18 : 0.12),
                                                color: theme?.colors?.primary,
                                                borderColor: withAlpha(theme?.colors?.primary || '#000000', 0.4),
                                            }}
                                        >
                                            Resolved
                                        </span>
                                    )}
                                </div>
                                <div className="font-medium text-sm mb-1 opacity-80">{item.category}</div>
                                <div className="text-sm whitespace-pre-wrap">{item.message}</div>
                                {item.attachment_urls && item.attachment_urls.length > 0 && (
                                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        {item.attachment_urls.map((url: string) => (
                                            <div key={url} className="border rounded-lg overflow-hidden" style={{ borderColor: theme?.colors?.border }}>
                                                {isVideoUrl(url) ? (
                                                    <video src={url} controls className="w-full max-h-40 bg-black" />
                                                ) : (
                                                    <img src={url} alt="Attachment" className="w-full max-h-40 object-cover" />
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}
